import { useState, useEffect } from "react";
import { useShoppingList } from "../contexts/ShoppingListContext";
import { ConfirmActionModal } from "../components/modals/ConfirmActionModal";
import { AiShoppingListFillModal } from "../components/modals/AiShoppingListFillModal";
import { useMealPlan } from "../contexts/MealPlanContext";
import { useIngredientUnits } from "../contexts/IngredientUnitContext";
import { ShoppingListPageHeader } from "../components/ShoppingListPage/ShoppingListPageHeader";
import { LoadingComponent } from "../components/LoadingComponent";
import { ShoppingListSection } from "../components/ShoppingListPage/ShoppingListSection";
import { EmptyComponentWithAnimation } from "../components/ShoppingListPage/EmptyComponentWithAnimation";
import { ShoppingListParsingApi } from "../api/ShoppingListParsingApi";
import { ShoppingCart } from "lucide-react";

// const categories = ["Produce", "Proteins", "Dairy", "Pantry", "None"]; // Kept for future enhancements
const phrases = ["Dont forget toilet paper!"];

export function ShoppingListPage() {
  const {
    shoppingListObject,
    addItemsToShoppingList,
    removeItemsFromShoppingListById,
    toggleItemCheck,
    clearShoppingList,
    fetchShoppingList,
    shoppingWindow,
    setShoppingWindow,
    customWeeks,
    setCustomWeeks,
    weekStartDates,
  } = useShoppingList();
  const { mealPlan } = useMealPlan();
  const { ingredientUnits } = useIngredientUnits();
  const quantityUnits = [...ingredientUnits.map((u) => u.unit), "None"];
  const [showClearModal, setShowClearModal] = useState(false);
  const [showAiFillModal, setShowAiFillModal] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [checkedItems, setCheckedItems] = useState(0);
  const [loading, setLoading] = useState(true);

  const handleAiParse = (text) => ShoppingListParsingApi.parseShoppingListText(text);

  useEffect(() => {
    const calculateProgress = () => {
      let total = 0;
      let checked = 0;
      shoppingListObject.forEach((item) => {
        total++;
        if (item.checked) {
          checked++;
        }
      });
      setTotalItems(total);
      setCheckedItems(checked);
    };

    if (shoppingListObject) {
      calculateProgress();
      setLoading(false);
    }
  }, [shoppingListObject]);

  useEffect(() => {
    fetchShoppingList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealPlan]);

  const toggleItem = (itemId) => {
    toggleItemCheck(itemId);
  };

  const deleteItem = (itemId) => {
    removeItemsFromShoppingListById(itemId);
  };

  const clearAllItems = () => {
    clearShoppingList();
    setShowClearModal(false);
  };

  const handleAddItem = (item) => {
    addItemsToShoppingList([item]);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <LoadingComponent message="Loading shopping list..." />
      </div>
    );
  }

  const progress = totalItems > 0 ? (checkedItems / totalItems) * 100 : 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <ShoppingListPageHeader
        onAddItem={handleAddItem}
        quantityUnits={quantityUnits}
        totalItems={totalItems}
        checkedItems={checkedItems}
        progress={progress}
        onClearClick={() => setShowClearModal(true)}
        shoppingWindow={shoppingWindow}
        onWindowChange={setShoppingWindow}
        customWeeks={customWeeks}
        onCustomWeeksChange={setCustomWeeks}
        weekStartDates={weekStartDates}
        onOpenAiFillModal={() => setShowAiFillModal(true)}
      />

      {/* Shopping List */}
      <div className="flex-1 overflow-auto p-4 md:p-8">
        {shoppingListObject.length === 0 ? (
          <EmptyComponentWithAnimation
            icon={ShoppingCart}
            title="No items in shopping list"
            description="Your shopping list is empty. Add items to get started."
            phrases={phrases}
          />
        ) : (
          <ShoppingListSection
            items={shoppingListObject}
            toggleItem={toggleItem}
            deleteItem={deleteItem}
            shoppingWindow={shoppingWindow}
            weekStartDates={weekStartDates}
          />
        )}
      </div>
      <ConfirmActionModal
        isOpen={showClearModal}
        closeModal={() => setShowClearModal(false)}
        heading="Clear Shopping List?"
        message="This will permanently remove all items from your shopping list. This action cannot be undone."
        confirmButtonText="Clear All"
        action={clearAllItems}
      />
      {showAiFillModal && (
        <AiShoppingListFillModal
          onGenerate={handleAiParse}
          onAddItems={addItemsToShoppingList}
          onClose={() => setShowAiFillModal(false)}
        />
      )}
    </div>
  );
}
